import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, FolderCheck, RotateCcw, Loader2 } from "lucide-react";
import { usePitch } from "../context/PitchContext";
import { Reveal, Eyebrow } from "./Reveal";
import { SampleTag } from "./SampleTag";

const items = [
  "Certificates",
  "Survey records",
  "Maintenance records",
  "Safety equipment checks",
  "Drills & inductions",
  "Defect log",
];

const PackList = ({ step }) => (
  <ul className="space-y-2.5">
    {items.map((t, i) => {
      const done = i < step;
      const active = i === step;
      return (
        <li key={t} className={`flex items-center justify-between rounded-xl border px-4 py-3 transition-colors duration-300 ${done ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}>
          <span className={`text-sm md:text-base ${done ? "text-[#0B192C]" : "text-slate-600"}`}>{t}</span>
          {done ? (
            <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600">
              <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
            </motion.span>
          ) : active ? <Loader2 className="h-5 w-5 animate-spin text-[#C87D55]" /> : <span className="h-6 w-6 rounded-full border border-slate-300" />}
        </li>
      );
    })}
  </ul>
);

const Ready = ({ onReset }) => (
  <motion.div
    data-testid="inspection-ready-result"
    initial={{ opacity: 0, scale: 1.4, rotate: -8 }}
    animate={{ opacity: 1, scale: 1, rotate: -3 }}
    transition={{ type: "spring", stiffness: 200, damping: 14 }}
    className="mt-6 flex flex-col items-start gap-4 rounded-2xl border-2 border-emerald-600 bg-emerald-50 p-5 md:flex-row md:items-center md:justify-between"
  >
    <span className="flex items-center gap-3 whitespace-nowrap font-display text-2xl text-emerald-800 md:text-4xl">
      <FolderCheck className="h-8 w-8" /> Inspection-ready.
    </span>
    <button data-testid="inspection-pack-reset-btn" onClick={onReset} className="flex items-center gap-2 text-sm text-emerald-800 underline-offset-4 hover:underline">
      <RotateCcw className="h-4 w-4" /> Run again
    </button>
  </motion.div>
);

export const InspectionPack = () => {
  const pitch = usePitch();
  const [step, setStep] = useState(-1);
  const running = step >= 0 && step < items.length;
  const done = step === items.length;

  useEffect(() => {
    if (!running) return undefined;
    const t = setTimeout(() => setStep((s) => s + 1), 520);
    return () => clearTimeout(t);
  }, [step, running]);

  return (
    <section data-testid="inspection-pack-section" className="bg-white px-6 py-24 md:px-10 md:py-36">
      <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">
        <Reveal className="flex flex-col">
          <Eyebrow>The one-click promise</Eyebrow>
          <h2 className="mt-4 font-display text-4xl leading-[1.05] tracking-tight text-[#0B192C] md:text-6xl">
            Inspection tomorrow? <span className="italic text-[#C87D55]">One click.</span>
          </h2>
          <motion.button
            data-testid="prepare-inspection-pack-btn"
            onClick={() => setStep(0)}
            disabled={running}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="mt-10 w-full rounded-full bg-[#C87D55] px-8 py-6 font-display text-2xl text-[#0B192C] shadow-[0_20px_60px_-15px_rgba(200,125,85,0.7)] transition-colors duration-300 hover:bg-[#D4A373] disabled:opacity-70 md:w-auto md:px-12 md:text-3xl"
          >
            {running ? "Assembling…" : "Prepare inspection pack"}
          </motion.button>
          <div className="mt-6"><SampleTag light testId="inspection-pack-sample-label">Sample · illustrative only, not real vessel data</SampleTag></div>
          <figure data-testid="skipper-photo" className="order-first mb-10 overflow-hidden rounded-[28px] rounded-br-[96px] lg:order-none lg:mb-0 lg:mt-10">
            <img src={pitch.helmImage} alt={`At the helm of ${pitch.vesselName}`} loading="lazy" className="aspect-[3/2] w-full object-cover object-[40%_center] transition-transform duration-700 hover:scale-105" />
          </figure>
        </Reveal>
        <Reveal delay={0.1}>
          <div data-testid="inspection-pack-panel" className="rounded-[28px] border border-[#1E3E62]/10 bg-[#F9FAFC] p-5 md:p-8">
            <div className="mb-5 flex items-center justify-between">
              <p className="font-display text-xl text-[#0B192C]">{pitch.vesselName} · Inspection pack</p>
            </div>
            <div className="mb-5 h-1.5 overflow-hidden rounded-full bg-slate-200">
              <motion.div className="h-full bg-emerald-600" animate={{ width: `${Math.max(step, 0) / items.length * 100}%` }} transition={{ duration: 0.4 }} />
            </div>
            <PackList step={step} />
            {done && <Ready onReset={() => setStep(-1)} />}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
